import { ReactNode, Ref, useId } from "react"

import { ReactComponent as IconClose } from "@common_assets/svg/cross.svg"
import { Box, IconButton, Stack, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"
import {
  IDashboardBehaviorRow,
  IDashboardBehaviorSelection,
} from "ts/interfaces/AdminDashboard"

import {
  BEHAVIOR_CATEGORY_COLOR,
  BEHAVIOR_TYPE,
  getBehaviorTrend,
  hasCategoryShare,
  RAW_INTERPOLATION,
} from "./behaviorFormat"
import BehaviorTrend from "./BehaviorTrend"

const Stat = ({
  value,
  valueColor = "mockup.neutral0",
  caption,
}: {
  value: ReactNode
  valueColor?: string
  caption: string
}) => (
  <Box>
    <Typography
      variant="body1"
      component="div"
      fontWeight={700}
      color={valueColor}
    >
      {value}
    </Typography>
    <Typography variant="body3" component="p" color="mockup.neutral30">
      {caption}
    </Typography>
  </Box>
)

// Names the requested behaviour, not the last loaded one.
const BehaviorFocusCard = ({
  selection,
  onClear,
  clearRef,
  count,
  share,
  trendRow,
}: {
  selection: IDashboardBehaviorSelection
  onClear: () => void
  clearRef?: Ref<HTMLButtonElement>
  // null while the chart's points still describe something else
  count: number | null
  share: number | null
  // Without a list row there is no trend
  trendRow?: IDashboardBehaviorRow
}) => {
  const { t } = useTranslation()
  const overlineId = useId()

  return (
    <Box
      role="group"
      aria-labelledby={overlineId}
      sx={{
        border: "1px solid",
        borderColor: "mockup.neutral90",
        borderLeft: "4px solid",
        borderLeftColor: BEHAVIOR_CATEGORY_COLOR[selection.type],
        borderRadius: "12px",
        p: 2,
      }}
    >
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <Box flex={1} minWidth={0}>
          <Typography
            id={overlineId}
            variant="body3"
            component="p"
            color="mockup.neutral30"
          >
            {t(BEHAVIOR_TYPE[selection.type].trendKey)}
          </Typography>
          <Typography
            variant="body1"
            color="mockup.neutral0"
            fontWeight={700}
            sx={{ wordBreak: "break-word" }}
          >
            {selection.title}
          </Typography>
        </Box>
        <IconButton
          ref={clearRef}
          size="small"
          data-export-ignore
          aria-label={t("adminDashboard.clearBehaviour", {
            ...RAW_INTERPOLATION,
            title: selection.title,
          })}
          onClick={onClear}
          sx={{ color: "mockup.neutral30" }}
        >
          <Box
            component={IconClose}
            aria-hidden
            sx={{ width: 20, height: 20 }}
          />
        </IconButton>
      </Stack>
      <Stack direction="row" flexWrap="wrap" columnGap={4} rowGap={1} mt={1.5}>
        <Stat
          value={count === null ? "—" : count.toLocaleString("en-US")}
          valueColor={BEHAVIOR_CATEGORY_COLOR[selection.type]}
          caption={t("adminDashboard.inThisPeriod")}
        />
        {hasCategoryShare(selection) && (
          <Stat
            value={share === null ? "—" : `${share}%`}
            caption={t(BEHAVIOR_TYPE[selection.type].shareKey)}
          />
        )}
        {trendRow && getBehaviorTrend(trendRow) && (
          <Stat
            value={<BehaviorTrend row={trendRow} />}
            caption={t("adminDashboard.vsPriorPeriod")}
          />
        )}
      </Stack>
    </Box>
  )
}

export default BehaviorFocusCard
